import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import { useMutation } from '@apollo/client';

import { InfiniteScrollComponent, captureException } from '@playbookux/playbook-web-core';
import {
  Container, Center, Text, TextPrimary, SectionHeader,
} from './styles';
import {
  Loading,
} from '../styles';
import Card from './card';
import ScreenerModal from './screener-modal';

import {
  getUpcomingConversations,
  getUnfinishedStudies,
  getInvitedStudies,
  checkScreenerAnswers,
} from '../helpers';

/** GraphQL */
import { REJECT_STUDY } from '../../../../core/graphql/screener-sessions';
import { PROJECT_TYPES_ENUMS } from '../../../../core/constants/projects';
import { TEXT_WAY_TO_GO } from './contants';

const Studies = (props) => {
  const {
    history,
    userId,
    hasMore,
    onLoadMore,
    testerStudies,
    ongoingStudies,
  } = props;

  /** GraphQL */
  const [rejectStudyMutation] = useMutation(REJECT_STUDY);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState({});
  const [rejectedStudies, setRejectedStudies] = useState([]);
  const [studies, setStudies] = useState(testerStudies);
  // const [sortedStudies, setSortedStudies] = useState({});

  const handleButtonClick = (study) => {
    const { surveys, testers, projectConsentForms = {} } = study || {};
    const [screenerSurvey] = surveys;
    const hasScreenerSurvey = (screenerSurvey && !_.isEmpty(screenerSurvey.questions)) || false;
    const hasConsentForms = !_.isEmpty(projectConsentForms);

    // Array of IDs of surveys taken by the user
    const screenersTaken = testers.map(({ screenerSession }) => screenerSession?.surveyId);
    const hasTakenLatestScreener = hasScreenerSurvey ? screenersTaken.includes(screenerSurvey.id) : true;

    // Open screener questions if survey is not empty and testers is empty
    // Or if TESTER exists, but the screener has not been answered yet
    if (((hasScreenerSurvey || hasConsentForms) && !hasTakenLatestScreener) || !checkScreenerAnswers(screenerSurvey, testers)) {
      setSelectedStudy(study);
    } else {
      history.push('/congratulations', {
        study,
      });
    }
  };

  const onModalClose = () => {
    setModalOpen(false);
    setSelectedStudy({});
  };

  const upcomingConversations = getUpcomingConversations(ongoingStudies);
  const invitedStudies = getInvitedStudies(ongoingStudies, upcomingConversations);
  const unfinishedStudies = getUnfinishedStudies(ongoingStudies, upcomingConversations, invitedStudies);
  const moderatedUnfinishedStudies = useMemo(() => ongoingStudies.filter((study) => {
    const [tester = {}] = study.testers;
    if (study.testType === PROJECT_TYPES_ENUMS.MODERATED && tester?.user?.id === userId && tester?.status === 'qualified') {
      return study;
    }
    return null;
  }), [studies, userId]);

  const notAppliedStudies = useMemo(() => (
    _.difference(studies, unfinishedStudies, moderatedUnfinishedStudies, invitedStudies, upcomingConversations)
  ), [studies, unfinishedStudies, moderatedUnfinishedStudies, invitedStudies, upcomingConversations]);

  useEffect(() => {
    setStudies(testerStudies);
  }, [testerStudies]);

  useEffect(() => {
    if (!studies) return;
    setStudies(studies.filter((study) => !rejectedStudies.includes(study.id)));
  }, [rejectedStudies]);

  useEffect(() => {
    if (!_.isEmpty(selectedStudy)) {
      setModalOpen(true);
    }
  }, [selectedStudy]);

  const handleRejectedTest = async (study, reason = 'screener_session') => {
    try {
      const data = await rejectStudyMutation({
        variables: {
          segmentId: study.id,
          disqualifiedReason: reason,
        },
      });
      // check if disqualifyRedirectUrl is present
      if (data?.data?.rejectStudy?.tester?.project?.disqualifyRedirectUrl) {
        // open it in a new tab
        window.open(data.data.rejectStudy.tester?.project?.disqualifyRedirectUrl, '_blank');
      }
      setRejectedStudies([...rejectedStudies, study.id]);
    } catch (e) {
      captureException('REJECT_STUDY_FAILURE', e);
    }
  };

  const renderStudies = (allStudies, data) => allStudies && allStudies.map((study, index) => {
    if (!rejectedStudies.includes(study.id)) {
      return (
        <Center key={study.id} {...{ [data]: index }}>
          <Card
            testerStudy={study}
            onButtonClick={handleButtonClick}
            onStudyRejected={handleRejectedTest}
          />
        </Center>
      );
    }

    return null;
  });

  const isLoading = (_.isEmpty(studies) && _.isEmpty(unfinishedStudies) && _.isEmpty(upcomingConversations) && _.isEmpty(invitedStudies));

  return (
    <Container>
      {!_.isEmpty(selectedStudy) && (
        <ScreenerModal
          open={modalOpen}
          testerStudy={selectedStudy}
          onClose={onModalClose}
          onStudyRejected={handleRejectedTest}
        />
      )}
      {isLoading ? (
        <Loading />
      )
        : (
          <InfiniteScrollComponent
            useWindow
            hasMore={hasMore}
            onLoadMore={onLoadMore}
          >
            {!_.isEmpty(invitedStudies) && (
              <>
                <SectionHeader>
                  <Text as="h3">A researcher personally invited you to a project</Text>
                  <TextPrimary>{TEXT_WAY_TO_GO}</TextPrimary>
                </SectionHeader>
                {renderStudies(invitedStudies, 'data-studies-invited')}
              </>
            )}

            {/*  Qualified moderated studies */}
            {!_.isEmpty(upcomingConversations) && (
              <>
                <SectionHeader>
                  <Text as="h3">Upcoming moderated conversations</Text>
                </SectionHeader>
                {renderStudies(upcomingConversations, 'data-studies-upcoming-moderated')}
              </>
            )}

            {/* Qualified studies */}
            {(!_.isEmpty(unfinishedStudies) || !_.isEmpty(moderatedUnfinishedStudies)) && (
              <>
                <SectionHeader>
                  <Text as="h3">Tests you&apos;ve been accepted to</Text>
                  <TextPrimary>You&apos;ve qualified for this study already</TextPrimary>
                </SectionHeader>
                {renderStudies([...unfinishedStudies, ...moderatedUnfinishedStudies], 'data-studies-qualified')}
              </>
            )}

            {/* Pending studies */}
            {!_.isEmpty(studies) && (
              <>
                <Text as="h3">Available tests you haven&apos;t applied to</Text>
                {renderStudies(notAppliedStudies, 'data-studies-not-applied')}
              </>
            )}
          </InfiniteScrollComponent>
        )}

    </Container>
  );
};

Studies.propTypes = {
  testerStudies: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      device: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  ongoingStudies: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      device: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  hasMore: PropTypes.bool.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
    location: PropTypes.shape(),
  }).isRequired,
};

export default withRouter(Studies);
